import { useCallback, useEffect, useState } from "react";
import { Database, Search, Loader2, Copy, Trash2, Download, ShieldAlert, ChevronLeft, ChevronRight, RefreshCw, ArrowLeft, Landmark, CheckCircle2, Users, Ticket, KeyRound, Scale, Siren, AlertTriangle } from "lucide-react";
import { useNexsysGate, getGateToken } from "./gate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const afetch = (path, opts = {}) =>
  fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getGateToken()}`, ...(opts.headers || {}) },
  });

const Panel = ({ title, icon: Icon, children, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl" data-testid={testid}>
    <div className="mb-4 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-cyan-400/80">
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    {children}
  </div>
);

const statusColor = (s) =>
  ({ approved: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10",
     rejected: "text-red-300 border-red-400/30 bg-red-500/10",
     deferred: "text-amber-300 border-amber-400/30 bg-amber-500/10" }[s] ||
   "text-cyan-300 border-cyan-400/30 bg-cyan-500/10");

function BaselinePanel() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { const r = await afetch("/admin/baseline/status"); if (r.ok) setSt(await r.json()); } catch { /* degrade */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true); setError("");
    try {
      const r = await afetch("/admin/baseline/initialize", { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setResult(`${d.status} · v${d.baseline_version} — ${d.created} created, ${d.skipped} preserved`); setConfirming(false); load(); }
      else setError(d.detail || "Initialization failed.");
    } catch { setError("Network error."); } finally { setBusy(false); }
  };

  const initialized = st?.status === "INITIALIZED";
  const upgrade = st?.status === "UPGRADE_AVAILABLE";
  const Row = ({ label, ok }) => (
    <div className="flex items-center gap-2 text-[12px]">
      <CheckCircle2 className={`h-3.5 w-3.5 ${ok ? "text-emerald-300" : "text-white/25"}`} />
      <span className="text-white/70">{label}</span>
      <span className={`ml-auto font-mono text-[10px] ${ok ? "text-emerald-300" : "text-white/35"}`}>{ok ? "ACTIVE" : "MISSING"}</span>
    </div>
  );

  return (
    <Panel title="SYSTEM SETUP — GOVERNED BASELINE" icon={Landmark} testid="admin-baseline-panel">
      {!st ? (
        <div className="flex items-center gap-2 py-4 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading baseline status…</div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className={`rounded-md border px-2 py-0.5 font-mono text-[11px] ${initialized ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300" : upgrade ? "border-amber-400/30 bg-amber-500/10 text-amber-300" : "border-cyan-400/30 bg-cyan-500/10 text-cyan-300"}`} data-testid="baseline-status">
              {initialized ? `INITIALIZED · v${st.baseline_version}` : upgrade ? `UPGRADE AVAILABLE · ${st.baseline_version} → ${st.available_version}` : "NOT INITIALIZED — READY TO INITIALIZE"}
            </span>
            <span className="text-[11px] text-white/40">Seeds governed system primitives only — never enterprise facts, decisions, or history.</span>
          </div>
          {initialized && (
            <div className="grid gap-1.5 rounded-xl border border-white/10 bg-black/20 p-3 sm:grid-cols-3" data-testid="baseline-components">
              <Row label="Continuum" ok={st.components?.continuum_root} />
              <Row label="Governed Memory" ok={(st.components?.categories?.memory_taxonomy || 0) >= (st.components?.expected?.memory_taxonomy || 1)} />
              <Row label="Governance Baseline" ok={(st.components?.categories?.governance_invariants || 0) >= (st.components?.expected?.governance_invariants || 1)} />
            </div>
          )}
          {!confirming ? (
            <button onClick={() => setConfirming(true)} disabled={busy}
              className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-50" data-testid="baseline-init-btn">
              <Landmark className="h-4 w-4" /> Initialize Governed Baseline
            </button>
          ) : (
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-3" data-testid="baseline-confirm-box">
              <p className="text-[12px] text-white/70">This initializes NexSys system-level governance, Continuum, and memory structures. It does not create enterprise facts, decisions, or historical data.</p>
              <div className="mt-2 flex gap-2">
                <button onClick={run} disabled={busy} className="flex items-center gap-2 rounded-lg bg-cyan-500/90 px-3 py-1.5 text-[13px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="baseline-confirm-btn">
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Confirm initialization
                </button>
                <button onClick={() => setConfirming(false)} className="rounded-lg border border-white/15 px-3 py-1.5 text-[13px] text-white/60 hover:border-white/30" data-testid="baseline-cancel-btn">Cancel</button>
              </div>
            </div>
          )}
          {result && <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-300" data-testid="baseline-result">{result}</div>}
          {error && <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="baseline-error">{error}</div>}
        </div>
      )}
    </Panel>
  );
}

const ROLE_OPTIONS = [
  { value: "viewer", label: "VIEWER" },
  { value: "member", label: "USER" },
  { value: "admin", label: "ADMIN" },
];
const roleLabel = (r) => ({ member: "USER", super_admin: "SUPER ADMIN" }[r] || (r || "").toUpperCase());

const SECRET_STATE_STYLE = {
  VERIFIED: "text-emerald-300 border-emerald-400/40", ROTATION_VERIFIED: "text-emerald-300 border-emerald-400/40",
  CONFIGURED_UNVERIFIED: "text-amber-300 border-amber-400/40", ROTATION_PENDING: "text-amber-300 border-amber-400/40",
  VERIFICATION_FAILED: "text-red-300 border-red-400/40", MISSING: "text-red-300 border-red-400/40",
  DISABLED: "text-white/50 border-white/20",
};

function SecretsPanel() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await afetch("/nexsys/secrets/registry");
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setData(d); setDenied(null); } else setDenied(d.detail || { reason: "Access denied" });
    } catch { /* degrade */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (integration, path, label) => {
    setBusy(integration + path); setMsg("");
    try {
      const r = await afetch(`/nexsys/secrets/${integration}/${path}`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      setMsg(r.ok ? `${integration}: ${label} → ${d.state || "ok"}${d.detail ? ` (${d.detail})` : ""}` : `${integration}: ${typeof d.detail === "string" ? d.detail : d.detail?.reason || "failed"}`);
      load();
    } finally { setBusy(""); }
  };

  return (
    <Panel title="INTEGRATION CREDENTIALS — SECRET MANAGEMENT BOUNDARY (OWNER + GOVERNED GRANT)" icon={KeyRound} testid="admin-secrets-panel">
      <p className="mb-3 text-[13px] text-white/50">
        Metadata only. Secret values live exclusively in the platform encrypted store (Deployment Panel → Secrets in production; backend/.env in preview) and are never stored, displayed, or returned by NexSys. Rotation: replace the value at the provider + platform store, Re-publish, then Mark Rotated and Verify here.
      </p>
      {denied && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-[13px] text-amber-200" data-testid="secrets-gate-notice">
          Gated: {denied.reason || String(denied)} — requires the env-anchored owner identity AND a governed <code>secrets.administer</code> grant (recorded by an independent admin on a Root Authority basis).
        </div>
      )}
      {msg && <div className="mb-2 text-[12px] text-cyan-200" data-testid="secrets-action-msg">{msg}</div>}
      {data?.registry?.map((e) => (
        <div key={e.integration} className="mb-2 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`secrets-row-${e.integration}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-mono text-sm text-white">{e.provider}</span>
              <span className={`ml-2 rounded-full border px-2 py-0.5 font-mono text-[10px] ${SECRET_STATE_STYLE[e.state] || "text-white/60 border-white/20"}`} data-testid={`secrets-state-${e.integration}`}>{e.state}</span>
            </div>
            <div className="flex gap-1.5">
              {e.verifiable && <button disabled={!!busy} onClick={() => act(e.integration, "verify", "verified")} className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:border-cyan-400/40" data-testid={`secrets-verify-${e.integration}`}>{busy === e.integration + "verify" ? "…" : "Test / Verify"}</button>}
              <button disabled={!!busy} onClick={() => act(e.integration, "rotation/assert", "rotation asserted")} className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:border-amber-400/40" data-testid={`secrets-rotate-${e.integration}`}>Mark Rotated</button>
              <button disabled={!!busy} onClick={() => act(e.integration, e.disabled ? "enable" : "disable", e.disabled ? "enabled" : "disabled")} className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:border-red-400/40" data-testid={`secrets-disable-${e.integration}`}>{e.disabled ? "Enable" : "Disable (kill switch)"}</button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {e.bindings?.map((b) => (
              <span key={b.runtime_binding_id} className="rounded bg-black/40 px-2 py-0.5 font-mono text-[10px] text-white/60" data-testid={`secrets-binding-${b.runtime_binding_id}`}>
                <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${b.present ? "bg-emerald-400" : "bg-red-400"}`} />{b.runtime_binding_id}
              </span>
            ))}
          </div>
          {e.verification?.checked_at && <div className="mt-1 font-mono text-[10px] text-white/40">last verification: {e.verification.status} · {e.verification.detail} · {e.verification.checked_at}</div>}
        </div>
      ))}
    </Panel>
  );
}

function UserManagementPanel() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(null); // {user, newRole}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { const r = await afetch("/admin/users"); if (r.ok) setData(await r.json()); } catch { /* degrade */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyChange = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      const r = await afetch(`/admin/users/${pending.user.id}/role`, { method: "PATCH", body: JSON.stringify({ role: pending.newRole }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setMsg(d.message); setPending(null); load(); }
      else setError(d.detail || "Role change rejected.");
    } catch { setError("Network error."); } finally { setBusy(false); }
  };

  return (
    <Panel title="USER MANAGEMENT — APPLICATION ROLES (SUPER ADMIN)" icon={Users} testid="admin-usermgmt-panel">
      <p className="mb-3 text-[12px] text-white/45">Application access only — changing a role never grants or modifies enterprise decision, governance, delegation, approval, or execution authority (EDIOS governs independently).</p>
      {!data ? (
        <div className="flex items-center gap-2 py-4 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading users…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left text-[12px]" data-testid="usermgmt-table">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] tracking-widest text-white/40">
                <th className="px-3 py-2">NAME</th><th className="px-3 py-2">EMAIL</th><th className="px-3 py-2">ROLE</th>
                <th className="px-3 py-2">STATUS</th><th className="px-3 py-2">TENANT</th><th className="px-3 py-2">CREATED</th>
                <th className="px-3 py-2">ROLE CHANGED</th><th className="px-3 py-2">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((u) => (
                <tr key={u.id} className="border-b border-white/5 text-white/80" data-testid={`usermgmt-row-${u.email}`}>
                  <td className="px-3 py-2">{u.name}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-white/60">{u.email}</td>
                  <td className="px-3 py-2"><span className={`rounded-md border px-1.5 py-0.5 text-[10px] ${u.role === "super_admin" ? "border-pink-400/30 text-pink-300" : u.role === "admin" ? "border-cyan-400/30 text-cyan-300" : u.role === "viewer" ? "border-amber-400/30 text-amber-300" : "border-emerald-400/30 text-emerald-300"}`}>{roleLabel(u.role)}</span></td>
                  <td className="px-3 py-2 text-white/50">{u.status}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/40">{u.tenant_id}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/40">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/40">{u.role_changed_by ? `${u.role_changed_by} · ${(u.role_changed_at || "").slice(0, 16).replace("T", " ")}` : "—"}</td>
                  <td className="px-3 py-2">
                    {u.role === "super_admin" || u.role_locked ? (
                      <span className="text-[10px] text-white/30">{u.role === "super_admin" ? "PROTECTED" : "ENV-LOCKED"}</span>
                    ) : (
                      <select defaultValue="" onChange={(e) => { if (e.target.value) { setPending({ user: u, newRole: e.target.value }); e.target.value = ""; } }}
                        className="rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[11px] text-white/70 outline-none" data-testid={`usermgmt-changerole-${u.email}`}>
                        <option value="" disabled>Change Role…</option>
                        {ROLE_OPTIONS.filter((o) => o.value !== u.role).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pending && (
        <div className="mt-3 rounded-xl border border-amber-400/30 bg-amber-500/5 p-3" data-testid="usermgmt-confirm-box">
          <p className="text-[13px] text-white/85">Change <strong>{pending.user.name}</strong> from <strong>{roleLabel(pending.user.role)}</strong> to <strong>{roleLabel(pending.newRole)}</strong>?</p>
          <p className="mt-1 text-[11px] text-white/50">This changes the user's NexSys application access. It does not grant or modify enterprise decision, governance, delegation, approval, or execution authority.</p>
          <div className="mt-2 flex gap-2">
            <button onClick={applyChange} disabled={busy} className="rounded-lg bg-cyan-500/90 px-3 py-1.5 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="usermgmt-confirm-btn">{busy ? "Applying…" : "Confirm change"}</button>
            <button onClick={() => setPending(null)} className="rounded-lg border border-white/15 px-3 py-1.5 text-[12px] text-white/60" data-testid="usermgmt-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
      {msg && <div className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-300" data-testid="usermgmt-result">{msg}</div>}
      {error && <div className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="usermgmt-error">{error}</div>}
    </Panel>
  );
}

function ViewerInvitesPanel() {
  const [invites, setInvites] = useState(null);
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", expires_at: "" });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { const r = await afetch("/admin/viewer-invites"); if (r.ok) setInvites((await r.json()).invites); } catch { /* degrade */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setError(""); setIssued(null);
    try {
      const r = await afetch("/admin/viewer-invites", { method: "POST", body: JSON.stringify({ ...form, expires_at: new Date(form.expires_at).toISOString() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setIssued(d); setForm({ recipient_name: "", recipient_email: "", expires_at: "" }); load(); }
      else setError(d.detail || "Failed to create invitation.");
    } catch { setError("Network error."); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    await afetch(`/admin/viewer-invites/${id}/revoke`, { method: "POST" }); load();
  };
  const stColor = { ACTIVE: "text-emerald-300", USED: "text-cyan-300", EXPIRED: "text-white/40", REVOKED: "text-red-300" };

  return (
    <Panel title="VIEWER INVITATIONS — INDIVIDUAL · EXPIRING · REVOCABLE (SUPER ADMIN)" icon={Ticket} testid="admin-invites-panel">
      <form onSubmit={create} className="mb-3 grid gap-2 sm:grid-cols-4">
        <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Recipient name (optional)"
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="invite-name-input" />
        <input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="Recipient email (optional)"
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="invite-email-input" />
        <input type="datetime-local" required value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/50" data-testid="invite-expiry-input" />
        <button type="submit" disabled={busy || !form.expires_at} className="rounded-xl bg-cyan-500/90 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="invite-create-btn">
          Create Viewer Invitation
        </button>
      </form>
      {issued && (
        <div className="mb-3 rounded-xl border border-cyan-400/30 bg-cyan-500/10 p-3" data-testid="invite-issued-box">
          <div className="text-[11px] text-white/60">Invitation token — shown ONCE, hashed at rest:</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="break-all rounded bg-black/40 px-2 py-1 font-mono text-[12px] text-cyan-200" data-testid="invite-token">{issued.token}</code>
            <button onClick={() => navigator.clipboard?.writeText(issued.token)} className="rounded-lg border border-white/15 p-1.5 text-white/60 hover:text-white" title="Copy"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
      {error && <div className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="invite-error">{error}</div>}
      <div className="space-y-1.5" data-testid="invite-list">
        {(invites || []).map((i) => (
          <div key={i.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-[11px]">
            <span className={`font-mono ${stColor[i.status] || "text-white/50"}`}>{i.status}</span>
            <span className="text-white/70">{i.recipient_name || i.recipient_email || "unnamed"}</span>
            <span className="font-mono text-white/35">by {i.created_by} · expires {(i.expires_at || "").slice(0, 16).replace("T", " ")}</span>
            {i.used_by_email && <span className="font-mono text-cyan-300/70">→ {i.used_by_email}</span>}
            {i.status === "ACTIVE" && (
              <button onClick={() => revoke(i.id)} className="ml-auto rounded-lg border border-red-400/30 px-2 py-0.5 text-[10px] text-red-300 hover:bg-red-500/10" data-testid={`invite-revoke-${i.id}`}>Revoke</button>
            )}
          </div>
        ))}
        {invites && !invites.length && <div className="py-2 text-[12px] text-white/35">No individual invitations issued yet. The legacy shared viewer code expires 2026-09-18 11:59 PM ET.</div>}
      </div>
    </Panel>
  );
}

function RecordsBrowser() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (p, q) => {
    setBusy(true);
    try {
      const r = await afetch(`/nexsys/admin/memory?page=${p}&page_size=25&search=${encodeURIComponent(q)}`);
      if (r.ok) setData(await r.json());
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(page, query); }, [page, query, load]);

  return (
    <Panel title="ENTERPRISE MEMORY — READ-ONLY BROWSER" icon={Database} testid="admin-records-panel">
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(search); }} className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title…"
            className="w-full rounded-xl border border-white/10 bg-black/30 py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="admin-search-input" />
        </div>
        <button type="submit" className="rounded-xl bg-cyan-500/90 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400" data-testid="admin-search-btn">Search</button>
      </form>
      {busy && !data ? (
        <div className="flex items-center gap-2 py-8 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading records…</div>
      ) : data && (
        <>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-[13px]" data-testid="admin-records-table">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] tracking-widest text-white/40">
                  <th className="px-3 py-2.5">TITLE</th><th className="px-3 py-2.5">STATUS</th>
                  <th className="px-3 py-2.5">CREATED</th><th className="px-3 py-2.5">DEDUP KEY</th><th className="px-3 py-2.5">OCC</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 text-white/80 hover:bg-white/[0.02]" data-testid="admin-record-row">
                    <td className="max-w-[340px] truncate px-3 py-2" title={r.title}>{r.title || "—"}</td>
                    <td className="px-3 py-2"><span className={`rounded-md border px-1.5 py-0.5 text-[11px] ${statusColor(r.status)}`}>{r.status || "—"}</span></td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-white/50">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="max-w-[220px] truncate px-3 py-2 font-mono text-[11px] text-white/40" title={r.dedup_key}>{r.dedup_key || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-white/50">{r.occurrences ?? 1}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-white/40">No records match.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between text-[12px] text-white/50">
            <span data-testid="admin-records-total">{data.total} record(s) · page {data.page} / {data.pages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1 || busy} onClick={() => setPage(page - 1)}
                className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 hover:border-cyan-400/40 disabled:opacity-40" data-testid="admin-prev-page"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
              <button disabled={page >= data.pages || busy} onClick={() => setPage(page + 1)}
                className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 hover:border-cyan-400/40 disabled:opacity-40" data-testid="admin-next-page">Next <ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

function DuplicatesTools() {
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [fuzzy, setFuzzy] = useState(false);

  const findDups = async () => {
    setBusy("find"); setError(""); setSummary(null);
    try {
      const r = await afetch(`/nexsys/admin/memory/duplicates?mode=${fuzzy ? "fuzzy" : "exact"}`);
      if (r.ok) setDups(await r.json()); else setError("Failed to scan for duplicates.");
    } catch { setError("Network error."); } finally { setBusy(""); }
  };

  const runCleanup = async () => {
    setBusy("clean"); setError("");
    try {
      const r = await afetch("/nexsys/admin/memory/cleanup", { method: "POST", body: JSON.stringify({ confirm: true, mode: fuzzy ? "fuzzy" : "exact" }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { setSummary(d); setDups(null); setConfirming(false); }
      else setError(d.detail || "Cleanup failed.");
    } catch { setError("Network error."); } finally { setBusy(""); }
  };

  return (
    <Panel title="DE-DUPLICATION — PREVIEW & CLEANUP" icon={Copy} testid="admin-dedup-panel">
      <label className="mb-3 flex cursor-pointer items-center gap-2 text-[12px] text-white/60" data-testid="admin-fuzzy-toggle">
        <input type="checkbox" checked={fuzzy} onChange={(e) => { setFuzzy(e.target.checked); setDups(null); setConfirming(false); }}
          className="h-3.5 w-3.5 accent-cyan-400" />
        Include near-duplicates (fuzzy — token-set Jaccard ≥ 0.6, collapses legacy paraphrase pairs)
      </label>
      <div className="flex flex-wrap gap-2">
        <button onClick={findDups} disabled={!!busy}
          className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-50" data-testid="admin-find-duplicates-btn">
          {busy === "find" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find duplicates (read-only)
        </button>
        {dups && dups.would_remove > 0 && !confirming && (
          <button onClick={() => setConfirming(true)} className="flex items-center gap-2 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-2 text-sm text-red-300 hover:bg-red-500/20" data-testid="admin-run-cleanup-btn">
            <Trash2 className="h-4 w-4" /> Propose retirement…
          </button>
        )}
      </div>
      {confirming && (
        <div className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 p-4" data-testid="admin-cleanup-confirm">
          <div className="flex items-center gap-2 text-sm text-red-200"><ShieldAlert className="h-4 w-4" /> This proposes a governed RETIREMENT Decision Object for {dups?.would_remove} older duplicate record(s). Nothing is deleted: the effect applies only after approval + governed execution (soft retirement, records preserved with lineage). Continue?</div>
          <div className="mt-3 flex gap-2">
            <button onClick={runCleanup} disabled={busy === "clean"} className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-400 disabled:opacity-50" data-testid="admin-cleanup-confirm-yes">
              {busy === "clean" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Yes, propose retirement
            </button>
            <button onClick={() => setConfirming(false)} className="rounded-lg border border-white/15 px-4 py-1.5 text-sm text-white/70 hover:border-white/30" data-testid="admin-cleanup-confirm-no">Cancel</button>
          </div>
        </div>
      )}
      {error && <div className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="admin-dedup-error">{error}</div>}
      {dups && (
        <div className="mt-4" data-testid="admin-duplicates-result">
          <div className="text-[13px] text-white/70">{dups.groups_count} duplicate group(s) ({dups.mode} mode) — cleanup would remove <span className="font-semibold text-red-300">{dups.would_remove}</span> record(s).</div>
          <div className="mt-2 max-h-[280px] space-y-2 overflow-y-auto pr-1">
            {dups.groups.map((g) => (
              <div key={g.dedup_key} className="rounded-lg border border-white/10 bg-black/20 p-3 text-[12px]" data-testid="admin-dup-group">
                <div className="font-mono text-[10px] text-white/35">{g.dedup_key}</div>
                <div className="mt-1 text-emerald-300">KEEP: {g.keep.title} <span className="text-white/40">({(g.keep.created_at || "").slice(0, 19)})</span></div>
                {g.remove.map((x) => <div key={x.id} className="text-red-300/80">REMOVE: {x.title} <span className="text-white/40">({(x.created_at || "").slice(0, 19)})</span></div>)}
              </div>
            ))}
            {!dups.groups.length && <div className="py-3 text-[13px] text-white/40">No duplicates found — memory is clean.</div>}
          </div>
        </div>
      )}
      {summary && (
        <div className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-[13px] text-emerald-200" data-testid="admin-cleanup-summary">
          {summary.proposed ? (
            <>Governed retirement PROPOSED — Decision Object <span className="font-mono">{(summary.decision_id || "").slice(0, 8)}</span>: {summary.would_retire} record(s) across {summary.groups_count} group(s). No records were deleted. Approve the decision, then authorize &amp; execute it — the effect applies through the memory_retirement choke point with a Governed Execution Instance.</>
          ) : (
            <>No duplicate governed memory records — nothing to retire.</>
          )}
          {summary.kept?.length > 0 && (
            <div className="mt-2 space-y-0.5 text-[12px] text-emerald-200/70">
              {summary.kept.map((k) => <div key={k.id}>Kept "{k.title}" (+{k.merged} occurrence(s) merged)</div>)}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function ExportTool() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const download = async () => {
    setBusy(true); setError("");
    try {
      const r = await afetch("/nexsys/admin/memory/export");
      if (!r.ok) { setError("Export failed."); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "enterprise_memory_export.json"; a.click();
      URL.revokeObjectURL(url);
    } catch { setError("Network error."); } finally { setBusy(false); }
  };
  return (
    <Panel title="DATA EXPORT — STREAMED JSON" icon={Download} testid="admin-export-panel">
      <p className="mb-3 text-[13px] text-white/50">Streams the full enterprise_memory collection to a downloadable JSON file, cursor-batched server-side (never loaded into memory at once).</p>
      <button onClick={download} disabled={busy}
        className="flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="admin-export-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download data
      </button>
      {error && <div className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="admin-export-error">{error}</div>}
    </Panel>
  );
}

function RecoveryEnrollCard() {
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState("");
  const [err, setErr] = useState("");
  const enroll = async () => {
    setBusy(true); setErr("");
    try {
      const r = await afetch("/nexsys/auth/recovery/enroll", { method: "POST", body: JSON.stringify({}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d.detail === "string" ? d.detail : "Enrollment failed"); }
      else setIssued(d.recovery_key || "");
    } catch { setErr("Network error"); }
    setBusy(false);
  };
  return (
    <div className="mb-4 rounded-xl border border-cyan-400/15 bg-cyan-400/[0.04] p-3" data-testid="recovery-enroll-card">
      <div className="font-mono text-[10px] font-semibold tracking-wider text-cyan-300">ACCOUNT RECOVERY — SELF-SERVICE FACTOR</div>
      <p className="mt-1 text-[12px] text-white/50">
        Generate your recovery key (shown once, stored hashed). "Forgot password?" on the sign-in screen emails a
        single-use 15-minute link to your account address; Super Admin recovery additionally requires this key.
        Security questions are supplemental only and never authorize recovery alone.
      </p>
      {issued ? (
        <div className="mt-2 rounded-lg border border-emerald-400/25 bg-emerald-400/[0.06] px-3 py-2" data-testid="recovery-key-issued">
          <div className="font-mono text-[10px] text-emerald-300">RECOVERY KEY — SHOWN ONCE, STORE OFFLINE</div>
          <div className="mt-1 select-all break-all font-mono text-[12px] text-white/85">{issued}</div>
        </div>
      ) : (
        <button onClick={enroll} disabled={busy} data-testid="recovery-enroll-btn"
          className="mt-2 rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-[12px] text-cyan-200 disabled:opacity-40">
          {busy ? "Enrolling…" : "Generate recovery key"}
        </button>
      )}
      {err && <div className="mt-2 text-[12px] text-red-300" data-testid="recovery-enroll-error">{err}</div>}
    </div>
  );
}

function AccountsPanel() {
  const [data, setData] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [pw, setPw] = useState("");
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await afetch("/nexsys/admin/users?page=1&page_size=50");
    if (r.ok) setData(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const doReset = async (email) => {
    setBusy(true); setNote(null);
    try {
      const r = await afetch("/nexsys/admin/users/reset-password", { method: "POST", body: JSON.stringify({ email, new_password: pw }) });
      const d = await r.json().catch(() => ({}));
      setNote({ email, ok: r.ok, text: r.ok ? `Password reset — ${d.sessions_revoked} session(s) revoked; they must sign in again.` : (d.detail || "Reset failed.") });
      if (r.ok) { setResetFor(null); setPw(""); }
    } catch { setNote({ email, ok: false, text: "Network error." }); }
    finally { setBusy(false); }
  };

  const roleColor = (ro) => ({ admin: "text-magenta-300 border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-300", viewer: "text-amber-300 border-amber-400/40 bg-amber-500/10" }[ro] || "text-cyan-300 border-cyan-400/40 bg-cyan-500/10");

  return (
    <Panel title="ACCOUNTS — ROLES & PASSWORD RESET" icon={ShieldAlert} testid="admin-accounts-panel">
      <RecoveryEnrollCard />
      {!data ? <div className="flex items-center gap-2 py-4 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
        <div className="space-y-1.5">
          {data.rows.map((u) => (
            <div key={u.email} className="rounded-xl border border-white/10 bg-black/20 px-3 py-2" data-testid={`admin-account-${u.email}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-[13px] text-white/80">
                  {u.email} <span className="text-white/35">· {u.name || "—"}</span>
                  <span className={`ml-2 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase ${roleColor(u.role)}`}>{u.role}</span>
                </div>
                {resetFor === u.email ? (
                  <div className="flex items-center gap-1.5">
                    <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6)"
                      className="w-44 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="admin-reset-pw-input" />
                    <button onClick={() => doReset(u.email)} disabled={busy || pw.length < 6}
                      className="rounded-lg bg-cyan-500 px-2.5 py-1 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-40" data-testid="admin-reset-pw-confirm">
                      {busy ? <Loader2 className="inline h-3 w-3 animate-spin" /> : "Set"}
                    </button>
                    <button onClick={() => { setResetFor(null); setPw(""); }} className="rounded-lg border border-white/15 px-2.5 py-1 text-[12px] text-white/60">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setResetFor(u.email); setPw(""); setNote(null); }}
                    className="rounded-lg border border-white/15 px-2.5 py-1 text-[12px] text-white/70 hover:border-cyan-400/40" data-testid={`admin-reset-pw-${u.email}`}>Reset password</button>
                )}
              </div>
              {note?.email === u.email && (
                <div className={`mt-1.5 rounded-lg border px-2.5 py-1 text-[12px] ${note.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-red-400/30 bg-red-500/10 text-red-300"}`} data-testid="admin-reset-note">{note.text}</div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-white/35">Roles for allowlisted emails (admin/viewer) are enforced from backend/.env and cannot be changed here. Resetting a password revokes that account's active sessions.</p>
    </Panel>
  );
}

function ConnectorsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const r = await afetch("/nexsys/connectors");
    if (r.ok) setData(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (cid, action) => {
    setBusy(cid + action); setNote(null);
    try {
      const r = await afetch(`/nexsys/connectors/${cid}/${action}`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      let text;
      if (action === "activate") {
        const det = typeof d.detail === "object" && d.detail !== null ? d.detail : d;
        text = det.decision_id
          ? `${det.detail} — approve or reject it in Governance (decision ${det.decision_id})`
          : det.detail || "Activation failed";
      } else {
        text = d.detail || (action === "sync" ? `Synced: ${JSON.stringify(d.counts)}` : d.detail || "OK");
      }
      setNote({ cid, ok: r.ok && d.ok !== false, text });
      if ((action === "sync" || action === "activate") && r.ok) load();
    } catch { setNote({ cid, ok: false, text: "Network error." }); }
    finally { setBusy(""); }
  };

  const setMode = async (cid, mode) => {
    setBusy(cid + "mode");
    try { await afetch(`/nexsys/connectors/${cid}`, { method: "PATCH", body: JSON.stringify({ mode }) }); load(); }
    finally { setBusy(""); }
  };

  const showSnapshot = async (cid) => {
    const r = await afetch(`/nexsys/connectors/${cid}/snapshot`);
    setPreview(r.ok ? { cid, ...(await r.json()) } : { cid, error: "No snapshot yet — run a sync first." });
  };

  const modeColor = (mo) => ({ live: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10", shadow: "text-cyan-300 border-cyan-400/40 bg-cyan-500/10" }[mo] || "text-white/40 border-white/15 bg-white/5");

  return (
    <Panel title="LIVE CONNECTORS — DATA SOURCE FRAMEWORK" icon={Database} testid="admin-connectors-panel">
      <p className="mb-3 text-[13px] text-white/50">Shadow mode syncs data without driving dashboards; Live mode makes the connector the source of truth for programs (API + AI grounding, parity guaranteed). The generic REST slot activates the day real credentials land in backend/.env.</p>
      {!data ? <div className="flex items-center gap-2 py-4 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
        <div className="space-y-2">
          {data.connectors.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`admin-connector-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-[13px] text-white/85">{c.name}</span>
                  <span className={`ml-2 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase ${modeColor(c.mode)}`}>{c.mode}</span>
                  {c.activation && (
                    <span className={`ml-2 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase ${c.activation.status === "active" ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300" : c.activation.status === "proposed" ? "border-blue-400/40 bg-blue-500/10 text-blue-300" : "border-white/15 bg-white/5 text-white/40"}`} data-testid={`admin-connector-activation-${c.id}`}>
                      activation {c.activation.status}
                    </span>
                  )}
                  <div className="mt-0.5 font-mono text-[10px] text-white/35">type {c.type} · last sync {c.last_sync ? c.last_sync.slice(0, 19).replace("T", " ") : "never"}{c.counts ? ` · ${c.counts.programs} programs, ${c.counts.risks} risks, ${c.counts.milestones} milestones` : ""}{c.last_test ? ` · test ${c.last_test.ok ? "PASS" : "FAIL"} ${c.last_test.at?.slice(0, 19).replace("T", " ")}` : ""}</div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <select value={c.mode} onChange={(e) => setMode(c.id, e.target.value)} disabled={!!busy || c.type === "servicenow"}
                    title={c.type === "servicenow" ? "Governed — status changes only via test + Governance-approved activation" : undefined}
                    className="rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white/75 outline-none disabled:opacity-40" data-testid={`admin-connector-mode-${c.id}`}>
                    <option value="disabled">disabled</option><option value="shadow">shadow</option><option value="live">live</option>
                  </select>
                  <button onClick={() => act(c.id, "test")} disabled={!!busy} className="rounded-lg border border-white/15 px-2.5 py-1 text-[12px] text-white/70 hover:border-cyan-400/40 disabled:opacity-40" data-testid={`admin-connector-test-${c.id}`}>Test</button>
                  {c.type === "servicenow" && (
                    <button onClick={() => act(c.id, "activate")} disabled={!!busy} className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-2.5 py-1 text-[12px] text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40" data-testid={`admin-connector-activate-${c.id}`}>
                      {busy === c.id + "activate" ? <Loader2 className="inline h-3 w-3 animate-spin" /> : "Activate (governed)"}
                    </button>
                  )}
                  <button onClick={() => act(c.id, "sync")} disabled={!!busy || c.mode === "disabled"} className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-2.5 py-1 text-[12px] text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-40" data-testid={`admin-connector-sync-${c.id}`}>
                    {busy === c.id + "sync" ? <Loader2 className="inline h-3 w-3 animate-spin" /> : "Sync now"}
                  </button>
                  <button onClick={() => showSnapshot(c.id)} className="rounded-lg border border-white/15 px-2.5 py-1 text-[12px] text-white/70 hover:border-cyan-400/40" data-testid={`admin-connector-snapshot-${c.id}`}>Snapshot</button>
                </div>
              </div>
              {note?.cid === c.id && (
                <div className={`mt-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${note.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-amber-400/30 bg-amber-500/10 text-amber-200"}`} data-testid="admin-connector-note">{note.text}</div>
              )}
              {preview?.cid === c.id && (
                <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5 text-[12px] text-white/60" data-testid="admin-connector-preview">
                  {preview.error ? preview.error : (
                    <>
                      <div className="mb-1 font-mono text-[10px] text-white/35">snapshot {preview.synced_at?.slice(0, 19).replace("T", " ")}</div>
                      {(preview.programs || []).map((p) => (
                        <div key={p.id}>{p.name} — {p.phase}, progress {p.progress}%, risk {p.risk}, health {p.health}</div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function SweepTool() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async () => {
    setBusy(true); setError("");
    try {
      const r = await afetch("/nexsys/admin/sweep");
      if (r.ok) setRes(await r.json()); else setError("Sweep failed to run.");
    } catch { setError("Network error."); } finally { setBusy(false); }
  };
  return (
    <Panel title="GROUNDING SWEEP — 7-VIEW SELF-TEST" icon={RefreshCw} testid="admin-sweep-panel">
      <p className="mb-3 text-[13px] text-white/50">Deterministically verifies every core view routes correctly and carries its real screen data (plus multi-view and duplicate-scan probes). No LLM cost.</p>
      <button onClick={run} disabled={busy}
        className="flex items-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-50" data-testid="admin-run-sweep-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Run sweep
      </button>
      {error && <div className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300">{error}</div>}
      {res && (
        <div className="mt-4" data-testid="admin-sweep-result">
          <div className={`mb-2 text-sm font-semibold ${res.pass ? "text-emerald-300" : "text-red-300"}`} data-testid="admin-sweep-verdict">
            {res.pass ? "PASS" : "FAIL"} — {res.passed}/{res.total} probes passed · {res.ran_at?.slice(0, 19).replace("T", " ")} UTC
          </div>
          <div className="space-y-1">
            {res.results.map((r) => (
              <div key={r.view} className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-1.5 text-[12px]" data-testid="admin-sweep-row">
                <span className="text-white/75">{r.view}</span>
                <span className={r.pass ? "text-emerald-300" : "text-red-300"}>{r.pass ? "✓ pass" : `✗ fail (routed to ${r.routed_to})`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

function MonitoringPanel() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let live = true;
    const load = async () => {
      try { const r = await afetch("/nexsys/monitoring/status"); if (r.ok && live) setSt(await r.json()); } catch { /* degrade */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, []);
  const unverified = !st || st.status === "Unverified";
  return (
    <Panel title="GOVERNANCE MONITORING — HEARTBEAT · FINDINGS" icon={Scale} testid="admin-monitoring-panel">
      {unverified ? (
        <div className="rounded-xl border border-amber-400/20 bg-amber-500/5 p-4 text-[12px] text-amber-200" data-testid="monitoring-unverified">
          Unverified — no monitoring check record exists yet. The 15-minute heartbeat will write the first governed check record.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4 text-[12px]" data-testid="monitoring-last-check">
            <span className={`rounded-full px-3 py-1 font-mono text-[10px] tracking-widest ${st.last_check.status === "ok" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
              {st.last_check.status === "ok" ? "● MONITORING ACTIVE" : "● LAST SWEEP ERROR"}
            </span>
            <span className="text-white/60">Last check: <span className="font-mono">{st.last_check.finished_at}</span></span>
            <span className="text-white/60">Trigger: <span className="font-mono">{st.last_check.trigger}</span></span>
            <span className="text-white/60">Open findings: <span className="font-mono">{st.open_count}</span></span>
          </div>
          <div className="mt-4 space-y-2" data-testid="monitoring-findings">
            {(st.open_findings || []).length === 0 && <div className="text-[12px] text-white/40">No open findings.</div>}
            {(st.open_findings || []).map((a) => (
              <div key={a.id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid={`monitoring-alert-${a.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-mono text-[10px] tracking-widest ${a.severity === "HIGH" ? "text-red-300" : a.severity === "MEDIUM" ? "text-amber-300" : "text-cyan-300"}`}>{a.kind}</span>
                  <span className="font-mono text-[10px] text-white/30">{a.created_at}</span>
                  <span className={`font-mono text-[10px] ${String(a.email_status).startsWith("SENT") ? "text-emerald-300" : String(a.email_status).startsWith("SEND_FAILED") ? "text-red-300" : "text-white/30"}`}>{String(a.email_status).slice(0, 44)}</span>
                </div>
                <p className="mt-1 text-[12px] text-white/65">{a.summary}</p>
                <p className="mt-0.5 font-mono text-[10px] text-white/35">evidence: {a.evidence?.collection} / {a.evidence?.doc_id} · check {a.check_id}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

function GovernancePanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(null);
  const [exc, setExc] = useState({ decision_id: "", decision_class: "", duration_h: 24, reason: "" });
  const [emg, setEmg] = useState({ asserters: "", allowed_actions: "retention.hold", bounds_duration_h: 1 });

  const load = useCallback(async () => {
    const r = await afetch("/nexsys/governance/overview");
    if (r.ok) setData(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async (key, path, body) => {
    setBusy(key); setNote(null);
    try {
      const r = await afetch(path, { method: "POST", body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      setNote({ ok: r.ok, text: r.ok ? "Recorded through the governed choke point." : JSON.stringify(j.detail || j).slice(0, 300) });
      await load();
    } finally { setBusy(""); }
  };

  const sodOn = data?.sod_policy && data.sod_policy.enabled !== false;
  return (
    <Panel title="GOVERNANCE — SoD · EMERGENCY AUTHORITY · DISCREPANCIES" icon={Scale} testid="admin-governance-panel">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-white/80"><ShieldAlert className="h-3.5 w-3.5 text-cyan-300" /> Segregation of Duties</div>
          <div className="text-[12px] text-white/50" data-testid="gov-sod-status">
            {sodOn ? <>ACTIVE — proposer ≠ approver enforced (policy <span className="font-mono">{data.sod_policy.id.slice(0, 8)}</span>, determination <span className="font-mono">{(data.sod_policy.determination_id || "").slice(0, 8)}</span>)</> : "No governed SoD policy defined — no enforcement."}
          </div>
          <button onClick={() => post("sod", "/nexsys/governance/sod-policy", { enabled: true })} disabled={busy === "sod"}
            className="mt-3 rounded-lg bg-cyan-500 px-3 py-1.5 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="gov-sod-enable-btn">
            {busy === "sod" ? "Recording…" : sodOn ? "Re-issue SoD policy" : "Define SoD policy (proposer ≠ approver)"}
          </button>
          <div className="mt-4 border-t border-white/10 pt-3">
            <div className="mb-2 text-[11px] font-semibold tracking-wider text-white/50">GOVERNED EXCEPTION (time-bound)</div>
            <div className="flex flex-wrap gap-2">
              <input value={exc.decision_id} onChange={(e) => setExc({ ...exc, decision_id: e.target.value })} placeholder="Decision ID"
                className="w-36 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="gov-exc-decision-input" />
              <input value={exc.decision_class} onChange={(e) => setExc({ ...exc, decision_class: e.target.value })} placeholder="or class"
                className="w-28 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="gov-exc-class-input" />
              <input type="number" value={exc.duration_h} onChange={(e) => setExc({ ...exc, duration_h: Number(e.target.value) })}
                className="w-16 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="gov-exc-hours-input" />
              <button onClick={() => post("exc", "/nexsys/governance/sod-exceptions", exc)} disabled={busy === "exc" || (!exc.decision_id && !exc.decision_class)}
                className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[12px] text-amber-200 hover:bg-amber-500/20 disabled:opacity-40" data-testid="gov-exc-grant-btn">Grant exception</button>
            </div>
            <div className="mt-2 space-y-1" data-testid="gov-exceptions-list">
              {(data?.sod_exceptions || []).slice(0, 4).map((e) => (
                <div key={e.id} className="text-[11px] text-white/50">{e.active ? "ACTIVE" : "EXPIRED/SUPERSEDED"} · {e.decision_id || e.decision_class} · until {String(e.expires_at).slice(0, 16)}Z</div>
              ))}
            </div>
          </div>
          <div className="mt-3 border-t border-white/10 pt-2">
            <div className="mb-1 text-[11px] font-semibold tracking-wider text-white/50">RECENT SoD CHECKS</div>
            <div className="space-y-1" data-testid="gov-sod-checks">
              {(data?.sod_checks || []).slice(0, 5).map((c) => (
                <div key={c.id} className={`text-[11px] ${c.outcome === "BLOCKED_SOD" ? "text-red-300" : "text-emerald-300/80"}`}>{c.outcome} · {(c.decision_id || "").slice(0, 8)} · {c.surface}</div>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-white/80"><Siren className="h-3.5 w-3.5 text-red-300" /> Emergency Authority</div>
          <div className="text-[12px] text-white/50" data-testid="gov-emergency-status">
            {data?.emergency_policy ? <>ACTIVE — asserters: {(data.emergency_policy.asserters || []).join(", ")} · actions: {(data.emergency_policy.allowed_actions || []).join(", ")} · bounds {data.emergency_policy.bounds_duration_h}h</> : "No emergency-authority policy — assertions grant nothing."}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <input value={emg.asserters} onChange={(e) => setEmg({ ...emg, asserters: e.target.value })} placeholder="asserter emails, comma-separated"
              className="w-56 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="gov-emg-asserters-input" />
            <input value={emg.allowed_actions} onChange={(e) => setEmg({ ...emg, allowed_actions: e.target.value })} placeholder="allowed actions"
              className="w-40 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="gov-emg-actions-input" />
            <input type="number" value={emg.bounds_duration_h} onChange={(e) => setEmg({ ...emg, bounds_duration_h: Number(e.target.value) })}
              className="w-16 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="gov-emg-hours-input" />
            <button onClick={() => post("emg", "/nexsys/governance/emergency-policy", {
              asserters: emg.asserters.split(",").map((s) => s.trim()).filter(Boolean),
              allowed_actions: emg.allowed_actions.split(",").map((s) => s.trim()).filter(Boolean),
              bounds_duration_h: emg.bounds_duration_h,
            })} disabled={busy === "emg" || !emg.asserters}
              className="rounded-lg border border-red-400/40 bg-red-500/10 px-3 py-1 text-[12px] text-red-200 hover:bg-red-500/20 disabled:opacity-40" data-testid="gov-emg-save-btn">Save policy</button>
          </div>
          <div className="mt-3 border-t border-white/10 pt-2">
            <div className="mb-1 text-[11px] font-semibold tracking-wider text-white/50">EMERGENCY USE (distinct log) · POST-HOC REVIEWS</div>
            <div className="space-y-1" data-testid="gov-emergency-log">
              {(data?.emergency_actions || []).slice(0, 4).map((a) => (
                <div key={a.id} className={`text-[11px] ${a.outcome === "BLOCKED" ? "text-red-300" : "text-amber-200"}`}>{a.outcome} · {a.action} · {a.actor} · {String(a.at).slice(0, 16)}Z</div>
              ))}
              {(data?.posthoc_reviews || []).slice(0, 3).map((r) => (
                <div key={r.id} className="text-[11px] text-white/50">REVIEW {r.status} · action {(r.emergency_action_id || "").slice(0, 8)}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-white/80"><AlertTriangle className="h-3.5 w-3.5 text-amber-300" /> Memory ↔ Governed-State Discrepancies (governed state prevails)</div>
        <div className="space-y-1" data-testid="gov-discrepancies-list">
          {(data?.discrepancies || []).length === 0 && <div className="text-[12px] text-white/40">No discrepancies detected.</div>}
          {(data?.discrepancies || []).slice(0, 8).map((d) => (
            <div key={d.id || `${d.memory_id}-${d.field}`} className="text-[11px] text-amber-200/90" data-testid="gov-discrepancy-row">
              memory <span className="font-mono">{(d.memory_id || "").slice(0, 8)}</span> claims {d.program} {d.field}={d.memory_value} · governed value {d.governed_value} · {String(d.detected_at).slice(0, 16)}Z
            </div>
          ))}
        </div>
      </div>
      {note && <div className={`mt-3 rounded-lg border px-3 py-2 text-[12px] ${note.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-red-400/30 bg-red-500/10 text-red-300"}`} data-testid="gov-note">{note.text}</div>}
    </Panel>
  );
}

function AuthorityGrantsPanel() {
  const [roots, setRoots] = useState([]);
  const [grants, setGrants] = useState([]);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(null);
  const [root, setRoot] = useState({ principal: "", basis: "", reference: "", scope: "secrets.administer" });
  const [grant, setGrant] = useState({ grantee_email: "", basis_root_authority_id: "", expires_at: "" });

  const load = useCallback(async () => {
    const [r1, r2] = await Promise.all([afetch("/nexsys/authority/roots"), afetch("/nexsys/secrets/admin/grants")]);
    if (r1.ok) setRoots((await r1.json()).roots || []);
    if (r2.ok) setGrants((await r2.json()).grants || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async (key, path, body) => {
    setBusy(key); setNote(null);
    try {
      const r = await afetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
      const j = await r.json().catch(() => ({}));
      setNote({ ok: r.ok, text: r.ok ? "Recorded." : JSON.stringify(j.detail || j).slice(0, 280) });
      await load();
      return r.ok;
    } finally { setBusy(""); }
  };

  return (
    <Panel title="ROOT AUTHORITY & SECRET ADMINISTRATION GRANTS" icon={Landmark} testid="admin-authority-grants-panel">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 text-[12px] font-semibold text-white/80">Root Authority (ROOT-1 — representation, never creation)</div>
          <div className="flex flex-wrap gap-2">
            <input value={root.principal} onChange={(e) => setRoot({ ...root, principal: e.target.value })} placeholder="Authority principal (human)"
              className="w-52 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="root-principal-input" />
            <input value={root.basis} onChange={(e) => setRoot({ ...root, basis: e.target.value })} placeholder="Basis (charter/mandate)"
              className="w-48 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="root-basis-input" />
            <input value={root.reference} onChange={(e) => setRoot({ ...root, reference: e.target.value })} placeholder="Reference"
              className="w-40 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="root-reference-input" />
            <input value={root.scope} onChange={(e) => setRoot({ ...root, scope: e.target.value })} placeholder="Scope (comma-separated)"
              className="w-48 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="root-scope-input" />
            <label className="flex items-center gap-1 text-[11px] text-white/45">until
              <input type="datetime-local" value={root.effective_until || ""} onChange={(e) => setRoot({ ...root, effective_until: e.target.value })}
                title="effective_until — leave blank for an open-ended root (operative until revoked)"
                className="rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="root-effective-until-input" />
            </label>
            <button onClick={() => post("root", "/nexsys/authority/roots", {
              authority_source: { principal: root.principal, type: "human", basis: root.basis, reference: root.reference },
              represented_scope: root.scope.split(",").map((s) => s.trim()).filter(Boolean),
              registration_class: "ENTERPRISE_ATTESTATION",
              ...(root.effective_until ? { effective_until: new Date(root.effective_until).toISOString() } : {}),
            })} disabled={busy === "root" || !root.principal || !root.basis}
              className="rounded-lg bg-cyan-500 px-3 py-1.5 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="root-register-btn">Register root</button>
          </div>
          <div className="mt-3 space-y-1" data-testid="roots-list">
            {roots.slice(0, 6).map((r) => (
              <div key={r.id} className="text-[11px] text-white/50" data-testid="root-row">
                <span className="font-mono">{r.id.slice(0, 8)}</span> · <span className={r.status === "ACTIVE" ? "text-emerald-300" : "text-red-300"}>{r.status}</span> · {(r.authority_source || {}).principal} · scope [{(r.represented_scope || []).join(", ")}] · {r.effective_until ? `until ${r.effective_until.slice(0, 16).replace("T", " ")}` : "no expiry (until revoked)"}
              </div>
            ))}
            {roots.length === 0 && <div className="text-[11px] text-white/40">No Root Authority registered.</div>}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="mb-2 text-[12px] font-semibold text-white/80">Secret Administration Grants (independent actor records; no self-grant; env-anchored grantee only)</div>
          <div className="flex flex-wrap gap-2">
            <input value={grant.grantee_email} onChange={(e) => setGrant({ ...grant, grantee_email: e.target.value })} placeholder="Grantee email (env-anchored owner)"
              className="w-60 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="grant-grantee-input" />
            <select value={grant.basis_root_authority_id} onChange={(e) => setGrant({ ...grant, basis_root_authority_id: e.target.value })}
              className="w-56 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="grant-root-select">
              <option value="">Basis Root Authority…</option>
              {roots.filter((r) => r.status === "ACTIVE").map((r) => (
                <option key={r.id} value={r.id}>{r.id.slice(0, 8)} · {(r.authority_source || {}).principal}</option>
              ))}
            </select>
            <input value={grant.expires_at} onChange={(e) => setGrant({ ...grant, expires_at: e.target.value })} placeholder="Expiry ISO (optional)"
              className="w-44 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="grant-expiry-input" />
            <button onClick={() => post("grant", "/nexsys/secrets/admin/grants", {
              grantee_email: grant.grantee_email, basis_root_authority_id: grant.basis_root_authority_id,
              expires_at: grant.expires_at || null,
            })} disabled={busy === "grant" || !grant.grantee_email || !grant.basis_root_authority_id}
              className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-[12px] text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40" data-testid="grant-issue-btn">Issue grant</button>
          </div>
          <div className="mt-3 space-y-1" data-testid="grants-list">
            {grants.slice(0, 6).map((g) => (
              <div key={g.id} className="flex items-center gap-2 text-[11px] text-white/50" data-testid="grant-row">
                <span className="font-mono">{g.id.slice(0, 8)}</span> · <span className={g.status === "ACTIVE" ? "text-emerald-300" : "text-red-300"}>{g.status}</span> · {g.grantee_email} · by {g.granted_by}
                {g.status === "ACTIVE" && (
                  <button onClick={() => post(`rv-${g.id}`, `/nexsys/secrets/admin/grants/${g.id}/revoke`)} disabled={busy === `rv-${g.id}`}
                    className="rounded border border-red-400/40 bg-red-500/10 px-2 py-0.5 text-[10px] text-red-200 hover:bg-red-500/20" data-testid="grant-revoke-btn">Revoke</button>
                )}
              </div>
            ))}
            {grants.length === 0 && <div className="text-[11px] text-white/40">No grants recorded.</div>}
          </div>
        </div>
      </div>
      {note && <div className={`mt-3 rounded-lg border px-3 py-2 text-[12px] ${note.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-red-400/30 bg-red-500/10 text-red-300"}`} data-testid="authority-grants-note">{note.text}</div>}
    </Panel>
  );
}

function JiraCredentialPanel() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    const r = await afetch("/nexsys/secrets/jira/rotation");
    if (r.ok) { setData(await r.json()); setDenied(null); }
    else setDenied((await r.json().catch(() => ({})))?.detail);
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async (key, path, body) => {
    setBusy(key); setNote(null);
    try {
      const r = await afetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
      const j = await r.json().catch(() => ({}));
      setNote({ ok: r.ok, text: r.ok ? "Recorded (value-free governed flow)." : JSON.stringify(j.detail || j).slice(0, 280) });
      await load();
    } finally { setBusy(""); }
  };

  if (denied) {
    return (
      <Panel title="JIRA CREDENTIAL ADMINISTRATION" icon={KeyRound} testid="admin-jira-cred-panel">
        <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200" data-testid="jira-cred-denied">
          Secret Administration authority required — admin/super_admin status alone confers nothing. ({typeof denied === "string" ? denied : denied?.code})
        </div>
      </Panel>
    );
  }
  const e = data?.entry;
  const latest = (data?.proposals || [])[0];
  return (
    <Panel title="JIRA CREDENTIAL ADMINISTRATION — VALUE-FREE (ATTESTATION MODEL)" icon={KeyRound} testid="admin-jira-cred-panel">
      <div className="grid gap-2 text-[12px] text-white/70 md:grid-cols-2" data-testid="jira-cred-meta">
        <div>Integration identity: <span className="font-mono text-white/90">{data?.identity || "—"}</span></div>
        <div>Base URL: <span className="font-mono text-white/90">{data?.base_url || "—"}</span></div>
        <div>Lifecycle status: <span className="font-mono text-cyan-300" data-testid="jira-cred-state">{e?.state || "NOT CONFIGURED"}</span></div>
        <div>Credential reference: <span className="font-mono text-white/90" data-testid="jira-cred-reference">{e?.credential_reference || "—"} (v{e?.credential_version || 0})</span></div>
        <div>Last verified: <span className="font-mono">{e?.verification?.checked_at ? String(e.verification.checked_at).slice(0, 19) + "Z" : "never"}</span></div>
        <div>Rotation: <span className="font-mono">{e?.rotation?.asserted_at ? (e.rotation.verified_at ? "VERIFIED" : "PENDING") : "none"}</span> · Integration: <span className={e?.disabled ? "text-red-300" : "text-emerald-300"}>{e?.disabled ? "DISABLED" : "ENABLED"}</span></div>
      </div>
      <div className="mt-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-[11px] text-white/40">
        No token is ever entered, stored, returned, or revealed here. Replace the token OUTSIDE NexSys in the platform-managed secret configuration, then run the governed rotation flow below.
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="rotation reason"
          className="w-52 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="jira-rot-reason-input" />
        <button onClick={() => post("prop", "/nexsys/secrets/jira/rotation/propose", { reason })} disabled={busy === "prop"}
          className="rounded-lg bg-cyan-500 px-3 py-1.5 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="jira-rot-propose-btn">Propose rotation</button>
        {latest && latest.status === "PROPOSED" && (
          <button onClick={() => post("appr", `/nexsys/secrets/jira/rotation/${latest.id}/approve`)} disabled={busy === "appr"}
            className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5 text-[12px] text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50" data-testid="jira-rot-approve-btn">Approve (SoD-checked)</button>
        )}
        {latest && latest.status === "APPROVED_ROTATION_PENDING" && (
          <button onClick={() => post("ver", `/nexsys/secrets/jira/rotation/${latest.id}/verify`)} disabled={busy === "ver"}
            className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-1.5 text-[12px] text-amber-200 hover:bg-amber-500/20 disabled:opacity-50" data-testid="jira-rot-verify-btn">Verify rotation (live probe)</button>
        )}
        <button onClick={() => post("kill", `/nexsys/secrets/jira/${e?.disabled ? "enable" : "disable"}`)} disabled={busy === "kill"}
          className="rounded-lg border border-red-400/40 bg-red-500/10 px-3 py-1.5 text-[12px] text-red-200 hover:bg-red-500/20 disabled:opacity-50" data-testid="jira-cred-kill-btn">{e?.disabled ? "Re-enable integration" : "Revoke / disable (kill switch)"}</button>
      </div>
      <div className="mt-3 space-y-1" data-testid="jira-rot-proposals">
        {(data?.proposals || []).slice(0, 5).map((p) => (
          <div key={p.id} className="text-[11px] text-white/50" data-testid="jira-rot-proposal-row">
            <span className="font-mono">{p.id.slice(0, 8)}</span> · {p.status} · proposed by {p.proposed_by}
            {p.approval ? ` · approved by ${p.approval.approved_by} (${p.approval.sod_outcome})` : ""}
            {p.credential_reference ? ` · ${p.credential_reference}` : ""}
          </div>
        ))}
      </div>
      {note && <div className={`mt-3 rounded-lg border px-3 py-2 text-[12px] ${note.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-red-400/30 bg-red-500/10 text-red-300"}`} data-testid="jira-cred-note">{note.text}</div>}
    </Panel>
  );
}

function PolicyRegistryPanel() {
  const [reg, setReg] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await afetch("/nexsys/governance/registry"); if (r.ok) setReg(await r.json()); } catch { /* degrade */ }
  })(); }, []);
  const pols = reg?.policies || [];
  return (
    <Panel title="GOVERNANCE POLICY & AUTHORITY REGISTRY — READ-ONLY (DOCUMENTARY)" icon={Landmark} testid="admin-policy-registry-panel">
      <p className="mb-2 rounded-md border border-amber-400/30 px-2 py-1 text-[11px] text-amber-300" data-testid="policy-registry-banner">
        DOCUMENTARY RATIFICATION ≠ SOFTWARE ACTIVATION — entries below are documentary records. Bounded activation (owner order 2026-10-12) covers R-002 expiry evaluation and the governed exception workflow ONLY; R-001 retention enforcement remains NOT implemented (open nonconformance). Any further activation requires separate owner authorization.
      </p>
      {!pols.length ? (
        <p className="text-[12px] text-white/45" data-testid="policy-registry-empty">No governance policies are filed in this environment. Production filing requires separate owner authorization (ADM-GOV-001 Phase 1 populates disposable validation databases only).</p>
      ) : (
        <div className="space-y-3 text-[12px] text-white/70">
          {pols.map((p) => (
            <div key={p.id} className="rounded-lg border border-white/10 p-2" data-testid={`policy-registry-${p.policy_key}`}>
              <div className="font-semibold text-white/85">{p.policy_key} — {p.title} <span className="text-[10px] text-white/40">({p.class})</span></div>
              {(p.versions || []).map((v) => (
                <div key={v.id} className="mt-1 text-[11px] text-white/55">
                  {v.version_label} · {v.document_name} · sha256 {String(v.document_sha256).slice(0, 16)}… · {v.documentary_status} · <span className="text-amber-300/80">{v.activation_status}</span>
                </div>
              ))}
            </div>
          ))}
          {(reg.ratifications || []).map((r) => (
            <div key={r.id} className="rounded-lg border border-white/10 p-2 text-[11px]" data-testid="policy-registry-ratification">
              <span className="text-white/85">{r.record_name}</span> · decided {r.decision_timestamp} · approver {r.approver?.name} · independent second approver: {r.independent_second_approver} · enforcement: {r.production_enforcement} · {r.documentary_status} · record hash: {r.record_sha256}
            </div>
          ))}
          {(reg.exceptions || []).map((e) => (
            <div key={e.id} className="rounded-lg border border-white/10 p-2 text-[11px]" data-testid="policy-registry-exception">
              <span className="text-white/85">{e.exception_key}</span> · {e.scope_verbatim} · expires {e.expires_local} · {e.documentary_status} · {e.workflow_status}
            </div>
          ))}
          {(reg.authority_bases || []).map((b) => (
            <div key={b.id} className="rounded-lg border border-white/10 p-2 text-[11px]" data-testid="policy-registry-authority-basis">
              Authority basis: {b.holder} · {b.basis_type} · corporate instrument: {b.corporate_instrument_status} · {b.verification_status}
            </div>
          ))}
          <div className="text-[10px] text-white/35">Event chain: {reg.chain?.valid ? `VALID (${reg.chain.events} events)` : "INVALID — tamper-evidence triggered"} · append-only journal, read-only surface.</div>
        </div>
      )}
    </Panel>
  );
}

const EXC_EXPIRY_UTC = "2026-11-11T04:59:59+00:00";
const FOUNDER_LINE = "Founder-led; issuer and operator are the same person, covered by EXC-R002-001.";

function CeilingsDelegationsPanel() {
  const [roots, setRoots] = useState([]);
  const [rootId, setRootId] = useState("");
  const [operator, setOperator] = useState("info@nexis.team");
  const [account, setAccount] = useState(null);
  const [secureConfirmed, setSecureConfirmed] = useState(false);
  const [ceiling, setCeiling] = useState(null);
  const [delegations, setDelegations] = useState([]);
  const [evidence, setEvidence] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState("");
  const [note, setNote] = useState(null);

  const loadRoots = useCallback(async () => {
    const r = await afetch("/nexsys/authority/roots");
    if (!r.ok) return;
    const rows = (await r.json()).roots || [];
    setRoots(rows);
    const exc = rows.find((x) => x.status === "ACTIVE" && (x.represented_scope || []).includes("exception.decide"));
    setRootId((prev) => prev || (exc ? exc.id : ""));
  }, []);
  const loadLineage = useCallback(async (agent) => {
    const email = (agent || "").trim().toLowerCase();
    if (!email) return;
    const [c, d, e] = await Promise.all([
      afetch(`/nexsys/authority/ceilings/${encodeURIComponent(email)}`),
      afetch(`/nexsys/authority/delegations?agent_email=${encodeURIComponent(email)}`),
      afetch(`/nexsys/authority/agents/${encodeURIComponent(email)}/scope-evidence`),
    ]);
    if (c.ok) setCeiling(await c.json());
    if (d.ok) setDelegations((await d.json()).delegations || []);
    if (e.ok) setEvidence(await e.json());
  }, []);
  useEffect(() => { loadRoots(); }, [loadRoots]);
  useEffect(() => { loadLineage(operator); }, [loadLineage]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAccount = async () => {
    setBusy("acct"); setNote(null); setAccount(null); setSecureConfirmed(false);
    try {
      const r = await afetch(`/nexsys/admin/users?page_size=100`);
      const rows = r.ok ? (await r.json()).rows || [] : [];
      const hit = rows.find((u) => u.email === operator.trim().toLowerCase());
      setAccount(hit || { missing: true });
      await loadLineage(operator);
    } finally { setBusy(""); }
  };

  const mutate = async (key, method, path, body) => {
    setBusy(key); setNote(null); setConfirming("");
    try {
      const r = await afetch(path, { method, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      setNote({ ok: r.ok, text: r.ok ? "Recorded — governed write accepted." : JSON.stringify(j.detail || j).slice(0, 300) });
      await loadLineage(operator);
    } finally { setBusy(""); }
  };
  const setOpCeiling = () => mutate("ceil", "PUT", "/nexsys/authority/ceilings", {
    agent_email: operator.trim().toLowerCase(), allowed_actions: ["exception.decide"], basis_root_authority_id: rootId });
  const issueDelegation = () => mutate("dele", "POST", "/nexsys/authority/delegations", {
    agent_email: operator.trim().toLowerCase(), root_authority_id: rootId,
    task: { task_id: "exc-r002-001-operator",
      title: `EXC-R002-001 reversible retention/archive/legal-hold administration. ${FOUNDER_LINE}`,
      authorized_actions: ["exception.decide"] },
    expires_at: EXC_EXPIRY_UTC });

  const ConfirmBtn = ({ k, label, action, disabled, testid }) => confirming === k ? (
    <span className="flex items-center gap-2">
      <button onClick={action} disabled={busy === k}
        className="rounded-lg bg-amber-400 px-3 py-1.5 text-[12px] font-semibold text-black hover:bg-amber-300 disabled:opacity-50" data-testid={`${testid}-confirm`}>
        Confirm production write</button>
      <button onClick={() => setConfirming("")} className="rounded-lg border border-white/20 px-2 py-1.5 text-[11px] text-white/60" data-testid={`${testid}-cancel`}>Cancel</button>
    </span>
  ) : (
    <button onClick={() => setConfirming(k)} disabled={disabled || !!busy}
      className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 text-[12px] text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-40" data-testid={testid}>{label}</button>
  );

  const activeDelegation = delegations.find((d) => d.status === "ACTIVE");
  const selRoot = roots.find((r) => r.id === rootId);
  return (
    <Panel title="AUTHORITY CEILINGS & DELEGATIONS — FOUNDER ADMINISTRATION" icon={Scale} testid="admin-ceilings-delegations-panel">
      <p className="mb-3 rounded-md border border-amber-400/30 px-2 py-1 text-[11px] text-amber-300" data-testid="cd-restrictions-banner">
        exception.approve is NOT delegable here — two accounts controlled by one person are never independent approval; irreversible decisions remain PROPOSED and non-executable. Delegation expiry is fixed to EXC-R002-001 lapse (Nov 10 2026, 11:59:59 PM ET). Server-side enforcement (self-raise prohibition, issuer ≠ operator, scope ⊆ root) applies regardless of this UI.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="text-[12px] font-semibold text-white/80">1 · Root Authority basis</div>
          <select value={rootId} onChange={(e) => setRootId(e.target.value)}
            className="w-full rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="cd-root-select">
            <option value="">Select Root Authority…</option>
            {roots.filter((r) => r.status === "ACTIVE").map((r) => (
              <option key={r.id} value={r.id}>{r.id} · [{(r.represented_scope || []).filter((a) => a !== "exception.approve").join(", ")}]</option>
            ))}
          </select>
          {selRoot && (
            <div className="break-all font-mono text-[10px] text-white/45" data-testid="cd-root-full-id">
              FULL ID: {selRoot.id} · {selRoot.status} · {(selRoot.authority_source || {}).principal}
            </div>
          )}
          <div className="text-[12px] font-semibold text-white/80">2 · Operator account security check</div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={operator} onChange={(e) => { setOperator(e.target.value); setAccount(null); setSecureConfirmed(false); }}
              className="w-56 rounded-lg border border-white/15 bg-black/40 px-2 py-1 text-[12px] text-white outline-none focus:border-cyan-400/50" data-testid="cd-operator-input" />
            <button onClick={checkAccount} disabled={busy === "acct" || !operator.trim()}
              className="rounded-lg border border-white/20 px-3 py-1.5 text-[12px] text-white/70 hover:border-cyan-400/40 disabled:opacity-40" data-testid="cd-check-account-btn">Check account</button>
          </div>
          {account && (
            <div className="rounded-lg border border-white/10 px-2 py-1.5 text-[11px] text-white/60" data-testid="cd-account-result">
              {account.missing ? <span className="text-red-300">No such account — delegation blocked.</span>
                : <>Account found · role <span className={account.role === "super_admin" || account.role === "admin" ? "text-emerald-300" : "text-amber-300"}>{account.role}</span>{account.role !== "super_admin" && account.role !== "admin" && " — not an admin role; governed endpoints will refuse it"}</>}
            </div>
          )}
          <label className="flex items-start gap-2 text-[11px] text-white/60" data-testid="cd-secure-confirm-label">
            <input type="checkbox" checked={secureConfirmed} onChange={(e) => setSecureConfirmed(e.target.checked)}
              disabled={!account || account.missing} className="mt-0.5" data-testid="cd-secure-confirm-checkbox" />
            <span>Founder confirmation: I have verified this operator account is secure (password rotated, sessions reviewed). This records my attestation only — it is not technical proof that prior sessions or tokens were revoked.</span>
          </label>
        </div>
        <div className="space-y-3 rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="text-[12px] font-semibold text-white/80">3 · Operator ceiling (hard-locked scope)</div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 font-mono text-[11px] text-cyan-200" data-testid="cd-ceiling-actions-locked">exception.decide</span>
            <ConfirmBtn k="ceil" label="Set ceiling" action={setOpCeiling} disabled={!rootId || !operator.trim()} testid="cd-set-ceiling-btn" />
          </div>
          <div className="text-[12px] font-semibold text-white/80">4 · Time-bound delegation (EXC-R002-001)</div>
          <div className="text-[11px] text-white/50">task exc-r002-001-operator · actions <span className="font-mono text-cyan-200">[exception.decide]</span> · expires <span className="font-mono" data-testid="cd-delegation-expiry-locked">{EXC_EXPIRY_UTC}</span><br />{FOUNDER_LINE}</div>
          <ConfirmBtn k="dele" label="Issue delegation" action={issueDelegation}
            disabled={!rootId || !operator.trim() || !secureConfirmed || !account || account.missing} testid="cd-issue-delegation-btn" />
          {!secureConfirmed && <div className="text-[10px] text-amber-300/80" data-testid="cd-delegation-gate-note">Delegation requires the operator security check and founder confirmation above.</div>}
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4" data-testid="cd-lineage">
        <div className="mb-2 text-[12px] font-semibold text-white/80">Authority lineage, effective permissions & evidence — {operator.trim().toLowerCase()}</div>
        <div className="grid gap-2 text-[11px] text-white/60 md:grid-cols-3">
          <div data-testid="cd-lineage-ceiling">
            <div className="text-white/80">Operative ceiling</div>
            {ceiling?.ceiling === "EXPLICIT" ? <>EXPLICIT · [{(ceiling.allowed_actions || []).join(", ")}] · <span className="font-mono">{String(ceiling.record_id).slice(0, 8)}</span></> : <span className="text-amber-300/80">{ceiling?.ceiling || "NONE"} — default-downward deny</span>}
          </div>
          <div data-testid="cd-lineage-delegation">
            <div className="text-white/80">Delegation</div>
            {activeDelegation ? <>ACTIVE · [{(activeDelegation.task?.authorized_actions || []).join(", ")}] · expires {activeDelegation.expires_at || "NONE"} · root <span className="font-mono">{String(activeDelegation.root_authority_id).slice(0, 8)}</span> · issued by {activeDelegation.issued_by}</> : "None active"}
          </div>
          <div data-testid="cd-lineage-effective">
            <div className="text-white/80">Effective authorized actions</div>
            {(evidence?.effective_authorized_actions || []).join(", ") || "NONE — every governed call denies"}
          </div>
        </div>
        {(evidence?.delegation_chains || []).slice(0, 3).map((ch, i) => (
          <div key={i} className="mt-2 break-all font-mono text-[10px] text-white/35" data-testid="cd-evidence-chain">chain: {JSON.stringify(ch).slice(0, 220)}</div>
        ))}
        <div className="mt-2 text-[10px] text-white/35">Evidence generated {evidence?.generated_at || "—"} · every evaluation and issuance is journaled in authority_audit; denials are pre-effect.</div>
      </div>
      {note && <div className={`mt-3 rounded-lg border px-3 py-2 text-[12px] ${note.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-red-400/30 bg-red-500/10 text-red-300"}`} data-testid="cd-note">{note.text}</div>}
    </Panel>
  );
}

export default function AdminPanel() {
  const { identity, logout } = useNexsysGate();
  if (!identity?.is_admin) {
    return (
      <div className="nx-root fixed inset-0 flex items-center justify-center bg-[#05070f] px-5" data-testid="admin-denied">
        <div className="max-w-[380px] rounded-2xl border border-red-400/30 bg-red-500/10 p-6 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-red-300" />
          <div className="mt-3 text-sm text-red-200">Administrator access required. Your account is not authorized for the data-operations panel.</div>
          <a href="/nexsys" className="mt-4 inline-block rounded-xl border border-white/15 px-4 py-2 text-sm text-white/70 hover:border-white/30">Back to Command Center</a>
        </div>
      </div>
    );
  }
  return (
    <div className="nx-root min-h-screen bg-[#05070f] text-white" data-testid="admin-panel">
      <div className="pointer-events-none fixed inset-0 nx-grid-bg opacity-30" />
      <div className="relative z-10 mx-auto max-w-5xl px-5 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-2xl font-bold"><span className="text-white">NexSys</span> <span className="text-cyan-400">Admin</span></div>
            <div className="mt-1 font-mono text-[10px] tracking-[0.25em] text-white/40">DATABASE OPERATIONS · SERVER-GATED · {identity.email?.toUpperCase()}</div>
          </div>
          <div className="flex gap-2">
            <a href="/nexsys" className="flex items-center gap-1.5 rounded-xl border border-white/15 px-3.5 py-2 text-sm text-white/70 hover:border-cyan-400/40" data-testid="admin-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Command Center</a>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-xl border border-white/15 px-3.5 py-2 text-sm text-white/70 hover:border-red-400/40" data-testid="admin-logout-btn"><RefreshCw className="h-3.5 w-3.5" /> Sign out</button>
          </div>
        </div>
        <div className="space-y-5">
          {identity?.role === "super_admin" && <GovernancePanel />}
          {identity?.role === "super_admin" && <MonitoringPanel />}
          {identity?.role === "super_admin" && <PolicyRegistryPanel />}
          {identity?.role === "super_admin" && <UserManagementPanel />}
          {identity?.role === "super_admin" && <SecretsPanel />}
          {identity?.role === "super_admin" && <AuthorityGrantsPanel />}
          {identity?.role === "super_admin" && <CeilingsDelegationsPanel />}
          {identity?.role === "super_admin" && <JiraCredentialPanel />}
          {identity?.role === "super_admin" && <ViewerInvitesPanel />}
          <BaselinePanel />
          <RecordsBrowser />
          <AccountsPanel />
          <ConnectorsPanel />
          <DuplicatesTools />
          <SweepTool />
          <ExportTool />
        </div>
      </div>
    </div>
  );
}
